import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { bridge } from '../lib/bridge';

type Step = 'phone' | 'code';

export function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  async function onRequest(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setHint(null);
    try {
      const res = await api.otpRequest(phone);
      if ('ok' in res && res.ok) {
        if ('devCode' in res && res.devCode) {
          setHint(`개발 모드 — 코드: ${res.devCode} (아무 6자리도 통과합니다)`);
        }
        setStep('code');
      } else if ('error' in res) {
        setError(res.error);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) setError('잠시 후 다시 시도해주세요 (분당 1회 / 시간당 5회 제한)');
        else setError(`요청 실패: ${err.message}`);
      } else setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await api.otpVerify(phone, code, 'web-dev');
      if ('token' in res) {
        bridge().setAuthToken(res.token, res.user_id, res.phone_e164);
        // P11 — 신규 사용자 또는 온보딩 미완료 → SignupWizard
        if (res.is_new_user || !res.onboarded_at) {
          navigate('/signup', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else if ('error' in res) {
        setError(res.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg)] px-xl">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-sm mb-xl">
          <div className="w-[10px] h-[10px] rounded-sm bg-[var(--accent)]" />
          <span className="font-display text-[20px] font-bold tracking-tight text-[var(--text-primary)]">Butler</span>
        </div>
        <h1 className="font-display text-[28px] font-medium leading-tight text-[var(--text-primary)] mb-sm">
          환영합니다.
        </h1>
        <p className="text-[var(--text-secondary)] text-[14px] mb-2xl">
          전화번호로 본인 인증 후 바로 사용하실 수 있어요.
        </p>

        {step === 'phone' ? (
          <form onSubmit={onRequest} className="space-y-md">
            <label className="block">
              <span className="block text-[13px] font-medium text-[var(--text-secondary)] mb-xs uppercase tracking-wider">전화번호</span>
              <input
                type="tel"
                inputMode="tel"
                placeholder="010-1234-5678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-md py-[12px] rounded-lg border border-[var(--border)] bg-[var(--bg)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                autoComplete="tel"
                required
              />
            </label>
            <button
              type="submit"
              disabled={busy || !phone}
              className="w-full py-[12px] rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] font-medium disabled:opacity-50 transition-colors"
            >
              {busy ? '발송 중…' : '인증코드 받기'}
            </button>
          </form>
        ) : (
          <form onSubmit={onVerify} className="space-y-md">
            <label className="block">
              <span className="block text-[13px] font-medium text-[var(--text-secondary)] mb-xs uppercase tracking-wider">인증코드 (6자리)</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full px-md py-[12px] rounded-lg border border-[var(--border)] bg-[var(--bg)] text-[var(--text-primary)] text-2xl tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-[var(--accent)] font-mono"
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </label>
            {hint && (
              <p className="text-[12px] text-[var(--text-secondary)] bg-[var(--accent-tint)] border border-[var(--border)] rounded-md p-md">
                {hint}
              </p>
            )}
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="w-full py-[12px] rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] font-medium disabled:opacity-50 transition-colors"
            >
              {busy ? '확인 중…' : '확인'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('phone'); setCode(''); setError(null); }}
              className="w-full py-sm text-[13px] text-[var(--text-secondary)]"
            >
              번호 다시 입력
            </button>
          </form>
        )}

        {error && (
          <p className="mt-md text-[13px] text-[var(--error)] bg-[var(--surface-elev)] border border-[var(--border)] rounded-md p-md">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
